<template>
  <teleport to="body">
    <div :class="['global-modal', 'fade', { show: show, active: show }]" v-show="show" tabindex="-1" role="dialog"
      aria-labelledby="globalModalLabel" aria-hidden="true">
      <div class="modal-overlay" @click.self="handleSelfClick">
        <div :class="['modal-dialog', 'modal-dialog-centered', `modal-${size}`]" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="globalModalLabel">
                {{ title }}
              </h5>
              <button @click="close" type="button" class="btn-close" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <slot />
            </div>
            <div class="modal-footer" v-if="$slots.footer">
              <slot name="footer" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>


//eslint-disable-next-line
const props = defineProps({
    show: {
      type: Boolean,
      required: true,
    },
    autoClose: { type: Boolean, default: true },
    title: {
      type: String,
      default: "Modal Title",
    },
    size: {
      type: String,
      default: 'md', // sm, md, lg, xl
      validator: (value) => ['sm', 'md', 'lg', 'xl'].includes(value)
    }
  });

  //eslint-disable-next-line
  const emit = defineEmits(['close'])

  const close = () => {
      emit("close");
    };
    const handleSelfClick = () => {
      if (props.autoClose)
        close();
    };


</script>

<style scoped>
/* Fix: Remove position: fixed from global-modal to prevent stacking issues */
.global-modal {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1050;
  /* Standard Bootstrap modal z-index */
  outline: 0;
}

.global-modal.show {
  display: block;
}

/* Fix: Use flexbox properly for centering */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  z-index: 1050;
  overflow-y: auto;
}

/* Fix: Ensure modal dialog is properly centered */
.modal-dialog {
  margin: auto;
  position: relative;
  width: auto;
  pointer-events: none;
  display: flex;
  align-items: center;
  min-height: calc(100% - 2rem);
}

.modal-dialog-centered {
  min-height: calc(100% - 2rem);
  display: flex;
  align-items: center;
}

.modal-content {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  pointer-events: auto;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  outline: 0;
}

/* RED THEME HEADER */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  background: linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%);
  /* Red gradient */
  border-top-left-radius: 11px;
  border-top-right-radius: 11px;
  flex-shrink: 0;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
  /* White text on red background */
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.btn-close {
  background: transparent url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23ffffff'%3e%3cpath d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L8 9.414l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z'/%3e%3c/svg%3e") center/1em auto no-repeat;
  border: none;
  opacity: 0.8;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0.25rem;
  transition: all 0.2s ease;
  cursor: pointer;
  flex-shrink: 0;
}

.btn-close:hover {
  opacity: 1;
  transform: scale(1.1);
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.modal-body {
  position: relative;
  flex: 1 1 auto;
  padding: 1.5rem;
  overflow-y: auto;
  max-height: calc(90vh - 180px);
  /* Adjusted for better fit */
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid #dee2e6;
  gap: 0.75rem;
  background: #f8f9fa;
  border-bottom-left-radius: 11px;
  border-bottom-right-radius: 11px;
  flex-shrink: 0;
}

/* Size variations */
.modal-sm {
  max-width: 400px;
}

.modal-md {
  max-width: 500px;
}

.modal-lg {
  max-width: 800px;
}

.modal-xl {
  max-width: 1140px;
}

/* Animation for modal appearance */
.global-modal.show .modal-dialog {
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-30px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Backdrop animation */
.global-modal.show .modal-overlay {
  animation: backdropFadeIn 0.3s ease-out;
}

@keyframes backdropFadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* Custom scrollbar for modal body */
.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Enhanced Mobile Responsive Design */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 0.5rem;
  }

  .modal-dialog {
    max-width: 95% !important;
    margin: 0.5rem auto;
  }

  .modal-dialog-centered {
    min-height: calc(100% - 1rem);
  }

  .modal-header {
    padding: 1rem 1.25rem;
  }

  .modal-body {
    padding: 1.25rem;
    max-height: calc(85vh - 150px);
  }

  .modal-footer {
    padding: 0.75rem 1.25rem;
    flex-wrap: wrap;
  }
}

@media (max-width: 576px) {
  .modal-overlay {
    padding: 0.25rem;
  }

  .modal-dialog {
    max-width: 98% !important;
    margin: 0.25rem auto;
  }

  .modal-dialog-centered {
    min-height: calc(100% - 0.5rem);
  }

  .modal-header {
    padding: 0.75rem 1rem;
  }

  .modal-title {
    font-size: 1.1rem;
  }

  .modal-body {
    padding: 1rem;
    max-height: calc(90vh - 130px);
  }

  .modal-footer {
    padding: 0.5rem 1rem;
    flex-direction: column;
  }

  .modal-footer ::v-deep .btn {
    width: 100%;
    margin: 0.25rem 0;
  }
}

/* Extra small devices */
@media (max-width: 400px) {
  .modal-dialog {
    max-width: 98% !important;
  }

  .modal-body {
    padding: 0.75rem;
    max-height: calc(95vh - 120px);
  }
}

/* Alternative Red Theme Variations - Uncomment to use */
/*
  Darker red theme:
  .modal-header {
    background: linear-gradient(135deg, #c62828 0%, #8e0000 100%);
  }

  Lighter red theme:
  .modal-header {
    background: linear-gradient(135deg, #ef5350 0%, #e53935 100%);
  }

  Solid red theme:
  .modal-header {
    background: #d32f2f;
  }
*/

/* Prevent body scroll when modal is open */
body.modal-open {
  overflow: hidden;
  padding-right: 0 !important;
  /* Prevent layout shift */
}

/* Touch-friendly scrolling for mobile */
.modal-body {
  -webkit-overflow-scrolling: touch;
}

/* Print styles */
@media print {
  .global-modal {
    position: absolute;
    background: white;
  }

  .modal-overlay {
    position: absolute;
    background: white;
  }

  .modal-content {
    box-shadow: none;
    border: 1px solid #000;
  }
}
</style>